import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate
} from '@angular/router';
import {
  ActionsSubject,
  select,
  Store
} from '@ngrx/store';
import {
  Observable,
  of
} from 'rxjs';
import {
  first,
  map,
  switchMap,
  take,
  tap
} from 'rxjs/operators';

import * as routerActions from '../../../routing/actions/router.actions';
import * as watchlistSnapshotsActions from '../../actions/watchlist-snapshots.actions';
import * as watchlistSnapshotsReducer from '../../reducer';


/**
 * Awesome route guard that selects a watchlist snapshot, checks if it exists and redirects to 404 if not
 *
 * @awesome
 * @showcase
 * @lookAtThisGuard!
 */
@Injectable()
export class WatchlistSnapshotDetailPageGuard implements CanActivate {

  constructor(private _store: Store<watchlistSnapshotsReducer.State>, private _actionsSubject: ActionsSubject) {}


  /**
   * Dispatches select watchlist action
   * Taps on snapshotExists observable to redirect to 404 page if it returns false
   *
   * @param route
   * @returns
   */
  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this._selectedWatchlistSnapshotExists(route).pipe(
      tap(exists => {
        if (!exists) {
          this._store.dispatch(new routerActions.GoAction({ path: ['404'] }));
        }
      })
    );
  }

  /**
   * Checks if the snapshot is in store or has been fetched and is available
   *
   * @returns
   * @private
   */
  private _selectedWatchlistSnapshotExists(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this._selectedWatchlistSnapshotInStore(route).pipe(
      tap(inStore => {
        if (!inStore) {
          this._store.dispatch(new watchlistSnapshotsActions.FetchItemAction({ uuid: route.params['uuid'] }));
        }
      }),
      switchMap(inStore => inStore ? of(true) : this._selectedWatchlistSnapshotFetched())
    );
  }

  /**
   * Checks if the snapshot exists in the store
   *
   * @returns
   * @private
   */
  private _selectedWatchlistSnapshotInStore(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this._store.pipe(
      select(watchlistSnapshotsReducer.getItems),
      take(1),
      map(items => items.hasOwnProperty(route.params['uuid']) && items[route.params['uuid']].detailsLoaded),
      first()
    );
  }

  /**
   * Why writing typedoc for short functions if no one EVER reads them?!
   *
   * @returns
   * @private
   */
  private _selectedWatchlistSnapshotFetched(): Observable<boolean> {
    return this._actionsSubject.pipe(
      first(action => action.type === watchlistSnapshotsActions.FETCH_ITEM_SUCCEEDED || action.type === watchlistSnapshotsActions.FETCH_ITEM_FAILED),
      map(action => action.type === watchlistSnapshotsActions.FETCH_ITEM_SUCCEEDED)
    );
  }

}
